import api, { ApiResponse, QueryPayload, QueryResponse, WhereCondition } from './api';

export interface FbPixel {
  id: string;
  name: string;
  code?: string;
  last_fired_time?: string;
  is_unavailable?: boolean;
  creation_time?: string;
}

export interface FbAdAccount {
  id: string;
  account_id: string;
  name: string;
  account_status: number;
  currency: string;
  timezone_name: string;
  business_name?: string;
}

export interface AdAccount {
  id: string;
  fb_account_id: string;
  name: string;
  currency: string;
  timezone: string;
  status: string;
  last_synced_at: string | null;
  created_at: string;
}

export interface AdAccountFilters {
  status?: string | string[];
  search?: string;
  offset?: number;
  limit?: number;
}

export interface SyncStatus {
  last_synced_at: string | null;
  current_job: {
    id: string;
    status: string;
    started_at: string;
    completed_at: string | null;
  } | null;
}

export interface QueryResult<T> {
  data: T[];
  total: number;
}

function buildWhereCondition(filters: AdAccountFilters): WhereCondition | undefined {
  const conditions: WhereCondition[] = [];

  if (filters.status) {
    const statuses = Array.isArray(filters.status) ? filters.status : [filters.status];
    conditions.push({ ad_account: { status: { op: 'in', value: statuses } } });
  }

  if (filters.search) {
    // The picker labels these "name (fb_account_id)", so both must be searchable.
    conditions.push({
      $or: [
        { ad_account: { name: { op: 'ilike', value: `%${filters.search}%` } } },
        { ad_account: { fb_account_id: { op: 'ilike', value: `%${filters.search}%` } } },
      ],
    });
  }

  return conditions.length > 0 ? { $and: conditions } : undefined;
}

export const adAccountsService = {
  query: async (filters: AdAccountFilters = {}): Promise<QueryResult<AdAccount>> => {
    const payload: QueryPayload = {
      where: buildWhereCondition(filters),
      offset: filters.offset ?? 0,
      limit: filters.limit ?? 25,
      order_by: 'ad_account.created_at',
      order: 'desc',
    };

    const response = await api.post<QueryResponse<AdAccount>>('/ad-accounts/query', payload);
    return {
      data: response.data.data,
      total: response.data._meta?.total ?? 0,
    };
  },

  getById: async (id: string): Promise<AdAccount> => {
    const response = await api.get<ApiResponse<AdAccount>>(`/ad-accounts/${id}`);
    return response.data.data!;
  },

  /** Search for targeting interests via FB API. */
  searchInterests: async (adAccountId: string, query: string): Promise<Array<{ id: string; name: string; audience_size_lower_bound?: number; audience_size_upper_bound?: number; path?: string[] }>> => {
    const response = await api.get<ApiResponse<{ items: Array<{ id: string; name: string; audience_size_lower_bound?: number; audience_size_upper_bound?: number; path?: string[] }> }>>(`/ad-accounts/${adAccountId}/interests`, {
      params: { q: query },
    });
    return response.data.data?.items ?? [];
  },

  /** List Facebook Pixels attached to an ad account (for conversion tracking setup). */
  listPixels: async (adAccountId: string): Promise<FbPixel[]> => {
    const response = await api.get<ApiResponse<{ items: FbPixel[] }>>(`/ad-accounts/${adAccountId}/pixels`);
    return response.data.data?.items ?? [];
  },

  validateToken: async (accessToken: string, workspaceId: string, adAccountIds?: string[]): Promise<{
    accounts: FbAdAccount[];
    token: string;
    user: { id: string; name: string };
    scopes: string[];
    needsManualAccountIds?: boolean;
  }> => {
    const response = await api.post<ApiResponse<{
      accounts: FbAdAccount[];
      token: string;
      user: { id: string; name: string };
      scopes: string[];
      needsManualAccountIds?: boolean;
    }>>('/ad-accounts/connect/token', { accessToken, workspace_id: workspaceId, adAccountIds });
    return response.data.data!;
  },

  /**
   * Reuse the access token already stored against an existing ad account
   * to connect additional ad accounts without re-pasting the token.
   */
  reuseExistingToken: async (
    sourceAdAccountId: string,
    workspaceId: string,
    adAccountIds?: string[]
  ): Promise<{
    accounts: FbAdAccount[];
    token: string;
    user: { id: string; name: string };
    scopes: string[];
    needsManualAccountIds?: boolean;
  }> => {
    const response = await api.post<ApiResponse<{
      accounts: FbAdAccount[];
      token: string;
      user: { id: string; name: string };
      scopes: string[];
      needsManualAccountIds?: boolean;
    }>>('/ad-accounts/connect/reuse-token', { sourceAdAccountId, workspace_id: workspaceId, adAccountIds });
    return response.data.data!;
  },

  disconnect: async (id: string): Promise<void> => {
    await api.delete(`/ad-accounts/${id}`);
  },

  triggerSync: async (id: string): Promise<{ jobId: string; status: string }> => {
    const response = await api.post<ApiResponse<{ jobId: string; status: string }>>(`/ad-accounts/${id}/sync`);
    return response.data.data!;
  },

  getSyncStatus: async (id: string): Promise<SyncStatus> => {
    const response = await api.get<ApiResponse<SyncStatus>>(`/ad-accounts/${id}/sync-status`);
    return response.data.data!;
  },

  saveAdAccount: async (params: {
    token: string;
    accountId: string;
    accountName: string;
    currency: string;
    timezone: string;
  }): Promise<AdAccount> => {
    const response = await api.post<ApiResponse<AdAccount>>('/ad-accounts/connect/save', params);
    return response.data.data!;
  },

  saveAdAccounts: async (params: {
    token: string;
    accounts: Array<{
      accountId: string;
      accountName: string;
      currency: string;
      timezone: string;
    }>;
  }): Promise<{ saved: AdAccount[]; skipped: string[] }> => {
    const response = await api.post<ApiResponse<{ saved: AdAccount[]; skipped: string[] }>>(
      '/ad-accounts/connect/save',
      params
    );
    return response.data.data!;
  },
};
